import React, { useMemo, useState } from "react";
import { GetActivitiesResponse, ActivityDTO } from "../../types/requestsOutputs";
import { ReactComponent as EditIcon } from "../../assets/images/edit-2-line.svg";
import { ReactComponent as DeleteIcon } from "../../assets/images/delete-bin-line.svg";
import { deleteActivity } from "../../api/activity";
import { activateProduct, deactivateProduct } from "../../api/product";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mui/material";
import DeleteModal from "../modals/DeleteModal";
import ToggleSwitch from "../common/ToggleSwitch";
import { useLanguage } from "../../context/LanguageContext";
import styles from "./Activity.module.css";
import trackStyles from "./SingleTrack.module.css";
import hubStyles from "../../pages/create/Hub.module.css";

interface Props {
    activities: GetActivitiesResponse;
    locale?: string;
    refetch?: () => void | Promise<void>;
}

interface ActivityPanelProps extends Props {
    onEdit?: (activity: ActivityDTO) => void;
}

/** Lower position first; 0 / unset last; then by name. */
function sortActivitiesByPosition(products: ActivityDTO[]): ActivityDTO[] {
    return [...products].sort((a, b) => {
        const posA = Number(a.position) || 0;
        const posB = Number(b.position) || 0;
        if (posA > 0 && posB > 0 && posA !== posB) return posA - posB;
        if (posA > 0 && posB === 0) return -1;
        if (posB > 0 && posA === 0) return 1;
        return (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" });
    });
}

export const ActivityPanel: React.FC<ActivityPanelProps> = ({ activities, refetch, onEdit }) => {
    const sortedProducts = useMemo(() => sortActivitiesByPosition(activities?.products ?? []), [activities?.products]);

    return (
        <div className={`${styles.panel} ${styles.panelCompact}`}>
            {sortedProducts.map((notif) => (
                <ActivityCard key={notif.id} notif={notif} refetch={refetch} onEdit={onEdit} />
            ))}
        </div>
    );
};

interface CardProps {
    notif: ActivityDTO;
    refetch?: () => void | Promise<void>;
    onEdit?: (activity: ActivityDTO) => void;
}

function activityDisplayUrl(notif: ActivityDTO): string {
    if (notif.url) return notif.url;
    const raw = notif.activityPCVLocalized;
    if (Array.isArray(raw) && raw[0]?.url) return raw[0].url;
    if (raw && typeof raw === "object" && "url" in raw) {
        return String((raw as { url?: string }).url || "");
    }
    return "";
}

function normalizeLangCode(code: string | undefined): string {
    if (!code) return "";
    const part = code.trim().split(/[-_]/)[0];
    return part.length >= 2 ? part.slice(0, 2).toUpperCase() : part.toUpperCase();
}

function pickLocalizedForCmsLanguage(notif: ActivityDTO, cmsUiLanguage: string): { name?: string; url?: string } {
    const raw = notif.activityPCVLocalized;
    const rows = Array.isArray(raw) ? raw : raw && typeof raw === "object" ? [raw] : [];
    const wanted = normalizeLangCode(cmsUiLanguage || "EN");
    const matchWanted = rows.find((r: any) => normalizeLangCode(r.language) === wanted);
    if (matchWanted) return matchWanted;
    const matchFr = rows.find((r: any) => normalizeLangCode(r.language) === "FR");
    if (matchFr) return matchFr;
    const matchEn = rows.find((r: any) => normalizeLangCode(r.language) === "EN");
    if (matchEn) return matchEn;
    return rows[0] || {};
}

const ActivityCard: React.FC<CardProps> = ({ notif, refetch, onEdit }) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { language: cmsUiLanguage } = useLanguage();

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isToggling, setIsToggling] = useState(false);
    const [optimisticActive, setOptimisticActive] = useState<boolean | null>(null);

    const localizedDisplay = useMemo(() => pickLocalizedForCmsLanguage(notif, cmsUiLanguage), [notif, cmsUiLanguage]);
    const displayName = localizedDisplay.name || notif.name || "";
    const displayUrl = localizedDisplay.url || activityDisplayUrl(notif);

    const isActive = optimisticActive !== null ? optimisticActive : Boolean(notif.isActive);

    const handleDelete = async () => {
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("No token found");
                return;
            }
            await deleteActivity({ id: notif.id, token });
            if (refetch) {
                await refetch();
            }
        } catch (error) {
            console.error("Failed to delete activity", error);
        }
    };

    const handleToggleStatus = async (nextChecked?: boolean) => {
        const next = nextChecked !== undefined ? nextChecked : !isActive;
        try {
            setIsToggling(true);
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("No token found");
                return;
            }
            setOptimisticActive(next);
            if (next) {
                await activateProduct({ token, productId: notif.id });
            } else {
                await deactivateProduct({ token, productId: notif.id });
            }
            if (refetch) {
                await refetch();
            }
            setOptimisticActive(null);
        } catch (error) {
            console.error("Failed to toggle activity status", error);
            setOptimisticActive(null);
        } finally {
            setIsToggling(false);
        }
    };

    const handleEdit = () => {
        onEdit?.(notif);
    };

    const picture = notif?.coverImage || null;

    return (
        <div className={`${trackStyles.rowCard} ${styles.activityRow}`}>
            {picture ? (
                <div className={trackStyles.thumb}>
                    <img src={picture} alt="" />
                </div>
            ) : (
                <div className={trackStyles.rowTypeIcon} aria-hidden>
                    <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                        <circle cx="12" cy="10" r="3" />
                    </svg>
                </div>
            )}

            <div className={trackStyles.rowInfo}>
                <p className={trackStyles.rowName}>{displayName || t("noNameProvided")}</p>
                <p className={trackStyles.rowMeta}>
                    {displayUrl ? (
                        <a
                            href={displayUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.rowMetaUrl}
                            title={displayUrl}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {displayUrl}
                        </a>
                    ) : (
                        "—"
                    )}
                    {notif.position ? (
                        <span>
                            {" "}
                            · {t("position")} {notif.position}
                        </span>
                    ) : null}
                </p>
            </div>

            <div className={trackStyles.statusToggle} onClick={(e) => e.stopPropagation()}>
                <span className={`${trackStyles.statusLabel}${isActive ? ` ${trackStyles.statusLabelActive}` : ""}`}>
                    {isActive ? t("active") : t("inactive")}
                </span>
                <ToggleSwitch
                    checked={isActive}
                    onChange={(checked) => {
                        if (!isToggling) void handleToggleStatus(checked);
                    }}
                />
            </div>

            <div className={trackStyles.rowSeparator} />

            <div className={trackStyles.rowActions} onClick={(e) => e.stopPropagation()}>
                <Tooltip title={t("edit")} arrow>
                    <EditIcon className={trackStyles.mutedIcon} onClick={handleEdit} />
                </Tooltip>
                <Tooltip title={t("delete")} arrow>
                    <DeleteIcon className={hubStyles.hubDeleteIconButton} onClick={() => setIsDeleteModalOpen(true)} />
                </Tooltip>
            </div>

            <DeleteModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleDelete}
                message={t("deleteActivityConfirmation")}
                title={t("deleteActivity")}
            />
        </div>
    );
};
